import React from "react";
import axios from "axios";
import { api, ZONES, PROPERTY_TYPES, resolveImg, API } from "../lib/api";
import { toast } from "sonner";
import { useT } from "../lib/i18n";
import useSEO from "../lib/useSEO";
import { breadcrumbLd, businessLd } from "../lib/jsonld";

const BENEFITS_KEYS = [
  { n: "01", tk: "list_asset.b1_t", dk: "list_asset.b1_d" },
  { n: "02", tk: "list_asset.b2_t", dk: "list_asset.b2_d" },
  { n: "03", tk: "list_asset.b3_t", dk: "list_asset.b3_d" },
  { n: "04", tk: "list_asset.b4_t", dk: "list_asset.b4_d" },
];

export default function ListAsset() {
  const { t } = useT();
  useSEO({
    title: "List Your Warehouse — WarehousesUAE.com (powered by Blueworld Realestate)",
    description: "Are you a warehouse owner in Dubai? List your asset with WarehousesUAE.com. Direct B2B tenant matching, AI-powered enquiries, zero listing fees — commission only on placement.",
    canonical: window.location.origin + "/list-your-asset",
    image: window.location.origin + "/og-default.png",
    jsonLd: [businessLd(), breadcrumbLd([{ name: "Home", url: "/" }, { name: "List Your Asset", url: "/list-your-asset" }])],
  });

  const [form, setForm] = React.useState({
    owner_name: "", owner_email: "", owner_phone: "", company: "",
    property_type: "warehouse", zone: "Other", address: "",
    size_sqft: "", expected_rent_aed: "", features: "", notes: "",
  });
  const [images, setImages] = React.useState([]); // array of URLs
  const [uploading, setUploading] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const newUrls = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await axios.post(`${API}/upload/public-image`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newUrls.push(r.data.url);
      }
      setImages((prev) => [...prev, ...newUrls]);
      toast.success(`${newUrls.length} ${t("toast.la_img_added")}`);
    } catch (err) {
      toast.error(`${t("toast.la_upload_fail")}: ` + (err?.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImg = (i) => setImages((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post("/list-asset", {
        ...form,
        size_sqft: parseInt(form.size_sqft) || 0,
        expected_rent_aed: form.expected_rent_aed ? parseInt(form.expected_rent_aed) : null,
        features: form.features.split(",").map((s) => s.trim()).filter(Boolean),
        images,
      });
      toast.success(t("list_asset.success"));
      setForm({ owner_name: "", owner_email: "", owner_phone: "", company: "", property_type: "warehouse", zone: "Other", address: "", size_sqft: "", expected_rent_aed: "", features: "", notes: "" });
      setImages([]);
      const url = res?.data?.whatsapp_redirect_url;
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(t("toast.la_fail"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="list-asset-page" className="bg-zinc-50 min-h-screen">
      {/* Hero */}
      <section className="bg-zinc-950 text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 bp-grid-dark opacity-30"></div>
        <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1601599963565-b7f49deb3d50?w=1800)" }}></div>
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/85 to-transparent"></div>
        <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-12 items-end">
          <div>
            <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[#FFCC00] mb-3">{t("list_asset.hero_kicker")}</div>
            <h1 className="font-display text-5xl md:text-7xl tracking-tighter leading-[0.95]">
              {t("list_asset.hero_title_a")}<br />{t("list_asset.hero_title_b")}<br />{t("list_asset.hero_title_c")}
            </h1>
          </div>
          <p className="text-zinc-300 leading-relaxed text-lg max-w-xl">
            {t("list_asset.hero_sub")}
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-white border-y border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="font-mono-tech text-[10px] uppercase text-[#031833] mb-2">// {t("list_asset.benefits_title")}</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tighter mb-10 max-w-2xl">FOUR REASONS<br />OWNERS CHOOSE US.</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200">
            {BENEFITS_KEYS.map((b) => (
              <div key={b.n} className="bg-white p-6">
                <div className="font-mono-tech text-[10px] text-[#031833] mb-3">{b.n} //</div>
                <h3 className="font-display text-xl tracking-tighter mb-2">{t(b.tk)}</h3>
                <p className="text-sm text-zinc-600">{t(b.dk)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-16">
        <div className="max-w-[1100px] mx-auto px-4 md:px-8">
          <div className="font-mono-tech text-[10px] uppercase text-[#031833] mb-2">// SUBMISSION</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tighter mb-8">{t("list_asset.submit_title")}</h2>

          <form onSubmit={submit} data-testid="list-asset-form" className="bg-white border border-zinc-900 p-6 md:p-10 grid md:grid-cols-2 gap-5" style={{ boxShadow: "10px 10px 0 0 #FFCC00" }}>
            <Field label={t("list_asset.f_name")} testid="la-name" required value={form.owner_name} onChange={(v) => setForm({ ...form, owner_name: v })} />
            <Field label={t("list_asset.f_company")} testid="la-company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} />
            <Field label={t("list_asset.f_email")} testid="la-email" type="email" required value={form.owner_email} onChange={(v) => setForm({ ...form, owner_email: v })} />
            <Field label={t("list_asset.f_phone")} testid="la-phone" required value={form.owner_phone} onChange={(v) => setForm({ ...form, owner_phone: v })} placeholder="+971 ..." />

            <Select label={t("list_asset.f_type")} testid="la-type" value={form.property_type} onChange={(v) => setForm({ ...form, property_type: v })} options={PROPERTY_TYPES.map((p) => ({ value: p.value, label: p.label }))} />
            <Select label={t("list_asset.f_zone")} testid="la-zone" value={form.zone} onChange={(v) => setForm({ ...form, zone: v })} options={ZONES.map((z) => ({ value: z, label: z }))} />

            <div className="md:col-span-2"><Field label={t("list_asset.f_address")} testid="la-address" required value={form.address} onChange={(v) => setForm({ ...form, address: v })} placeholder="Building, area, zone..." /></div>

            <Field label={t("list_asset.f_size")} testid="la-size" type="number" required value={form.size_sqft} onChange={(v) => setForm({ ...form, size_sqft: v })} />
            <Field label={t("list_asset.f_rent")} testid="la-rent" type="number" value={form.expected_rent_aed} onChange={(v) => setForm({ ...form, expected_rent_aed: v })} placeholder="Leave blank if flexible" />

            <div className="md:col-span-2"><Field label={t("list_asset.f_features")} testid="la-features" value={form.features} onChange={(v) => setForm({ ...form, features: v })} placeholder="Loading bays, chiller, mezzanine office..." /></div>

            <div className="md:col-span-2">
              <label className="font-mono-tech text-[10px] uppercase text-zinc-500 block mb-1">{t("list_asset.f_notes")}</label>
              <textarea data-testid="la-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 font-mono-tech text-sm outline-none focus:border-[#031833]" placeholder="Lease terms, current condition, availability date..."></textarea>
            </div>

            {/* Images */}
            <div className="md:col-span-2">
              <label className="font-mono-tech text-[10px] uppercase text-zinc-500 block mb-2">{t("list_asset.f_images")}</label>
              <div className="flex flex-wrap gap-2 mb-3">
                {images.map((img, i) => (
                  <div key={i} className="relative w-24 h-24 border border-zinc-200 overflow-hidden">
                    <img src={resolveImg(img)} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removeImg(i)} className="absolute top-0 right-0 bg-zinc-950 text-white text-xs px-1.5">×</button>
                  </div>
                ))}
                <label className="w-24 h-24 border border-dashed border-zinc-400 flex items-center justify-center cursor-pointer hover:border-[#031833]">
                  <input data-testid="la-image-upload" type="file" multiple accept="image/*" onChange={onFile} className="hidden" />
                  <span className="font-mono-tech text-[10px] text-zinc-500 text-center px-2">{uploading ? "..." : "+ ADD"}</span>
                </label>
              </div>
              <p className="font-mono-tech text-[10px] text-zinc-500">JPG / PNG / WebP. Multiple photos help us match faster.</p>
            </div>

            <div className="md:col-span-2 flex flex-col md:flex-row gap-3 items-center mt-3">
              <button data-testid="la-submit" disabled={submitting} className="w-full md:w-auto bg-zinc-950 text-white font-display text-xl tracking-tighter px-8 py-4 hover:bg-[#031833] disabled:opacity-50">
                {submitting ? t("list_asset.submitting") : t("list_asset.submit_btn")}
              </button>
              <p className="font-mono-tech text-[10px] text-zinc-500 md:ml-3">We respond within 24h. Your details remain confidential.</p>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, testid, value, onChange, ...rest }) {
  return (
    <div>
      <label className="font-mono-tech text-[10px] uppercase text-zinc-500 block mb-1">{label}</label>
      <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full border border-zinc-300 px-3 py-2.5 font-mono-tech text-sm outline-none focus:border-[#031833]" {...rest} />
    </div>
  );
}

function Select({ label, testid, value, onChange, options }) {
  return (
    <div>
      <label className="font-mono-tech text-[10px] uppercase text-zinc-500 block mb-1">{label}</label>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full border border-zinc-300 px-3 py-2.5 font-mono-tech text-sm outline-none focus:border-[#031833]">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
